import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { scrollToId } from "@/lib/api";

// Foto precedente (per ripristino rapido): https://images.unsplash.com/photo-1632583824020-937ae9564495?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBkYXJrJTIwa2l0Y2hlbnxlbnwwfHx8fDE3ODc4ODEwNTR8MA&ixlib=rb-4.1.0&q=85
const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/1c0e2dd4-1b05-42b9-a4d7-23faa2b554ac/images/689c5cc380857a3a63e87ffdf754e5491a4ba4165c6a8871cc720cbd4c6d0a73.jpeg";

const MaskedLine = ({ children, delay, className }) => (
  <div className="overflow-hidden">
    <motion.div
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, ease: [0.76, 0, 0.24, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
        <img src={HERO_IMG} alt="Interno ristrutturato Star Edil" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/30 to-black/5" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent md:from-black/75 md:via-black/20" />
        <div className="absolute inset-x-0 bottom-0 h-56 bg-ink/60 blur-2xl" style={{ maskImage: "linear-gradient(to top, black, transparent)" }} />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full max-w-[1600px] mx-auto px-6 lg:px-12 pb-28 lg:pb-36 pt-40">
        <MaskedLine delay={0.4}>
          <p className="font-oswald text-gold uppercase tracking-[0.25em] sm:tracking-[0.35em] text-sm mb-8 [text-shadow:_0_2px_10px_rgba(0,0,0,0.95)]" data-testid="hero-kicker">
            Star Edil srls — Impresa Edile
          </p>
        </MaskedLine>

        <h1 className="font-display leading-[0.95] tracking-tighter text-4xl sm:text-5xl lg:text-6xl xl:text-7xl max-w-5xl" data-testid="hero-title">
          <MaskedLine delay={0.55}>Ristrutturazioni</MaskedLine>
          <MaskedLine delay={0.7}>
            <span className="italic text-gold">chiavi in mano,</span>
          </MaskedLine>
          <MaskedLine delay={0.85}>senza pensieri.</MaskedLine>
        </h1>

        <MaskedLine delay={1.05}>
          <p className="mt-10 max-w-xl text-base md:text-lg text-muted-foreground leading-relaxed" data-testid="hero-subtitle">
            Costruiamo il futuro, dalle fondamenta. Un unico interlocutore per il tuo cantiere:
            progettazione, opere edili, impianti e finiture — consegnati pronti da vivere.
          </p>
        </MaskedLine>

        <MaskedLine delay={1.2}>
          <div className="mt-12 flex flex-wrap items-center gap-5">
            <button
              onClick={() => scrollToId("#preventivo")}
              data-testid="hero-cta-quote"
              className="btn-tracer group bg-gold text-ink font-oswald uppercase tracking-[0.15em] px-9 py-4 flex items-center gap-3 hover:bg-gold-hover transition-colors duration-300"
            >
              Richiedi un preventivo
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollToId("#lavori")}
              data-testid="hero-cta-portfolio"
              className="font-oswald uppercase tracking-[0.15em] px-9 py-4 border border-white/20 text-foreground hover:border-gold hover:text-gold transition-colors duration-300"
            >
              I nostri lavori
            </button>
          </div>
        </MaskedLine>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-10 right-6 lg:right-12 z-10 hidden md:flex flex-col items-center gap-3 text-muted-foreground"
      >
        <span className="font-oswald text-xs uppercase tracking-[0.3em] [writing-mode:vertical-rl]">Scorri</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown className="w-4 h-4 text-gold" />
        </motion.div>
      </motion.div>
    </section>
  );
};
